/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { ApplicationState } from 'store';
import { ConlicitasegState } from 'store/modules/conlicitaseg_store/types';
import { setFormQuote, clearFormQuote } from 'store/modules/conlicitaseg_store/actions';
import InsuranceStepIndicator from 'components/atoms/insurance_step_indicator';
import SquareLoader from 'components/atoms/square_loader';
import AiAutofillNotice, {
  IaBadge,
  AutofillValidationHint,
} from 'components/atoms/ai_autofill_notice';
import useQuoteFlow from 'hooks/useQuoteFlow';
import {
  formatCurrency,
  formatPercentage,
  parseCurrencyInput,
  parsePercentageInput,
  calculateCoverage,
  calculateEndDate,
  calculateMinStartDate,
  formatDate,
  modalities,
  normalizeModality,
  hasAutofilledField,
} from 'services/conlicitaseg';
import { InfoIcon } from './styles';

// Tela única de Licitação e Contrato, usada por todos os fluxos da cotação
const QuoteContractInfo: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { formProcess, guarantee_type, request, policyholder_register_junto, autofilled_fields } =
    useSelector<ApplicationState, ConlicitasegState>((state) => state.session.conlicitaseg);
  const { config, pathFor, stepIndicatorProps } = useQuoteFlow();

  // Na licitação o tipo vem em guarantee_type; nos demais fluxos, em type_warranty_id
  const isLicitante =
    formProcess.type_warranty_id === 'Licitante' || guarantee_type === 'participacao';
  const fieldColumn = isLicitante ? 'col-md-4 mb-4' : 'col-md-3 mb-3';

  const [form, setForm] = useState({
    bidding_modality: normalizeModality(formProcess.bidding_modality),
    bidding_edict: formProcess.bidding_edict || '',
    bidding_contract_number: formProcess.bidding_contract_number || '',
    bidding_process_number: formProcess.bidding_process_number || '',
    contract_days: formProcess.contract_days ? formProcess.contract_days.toString() : '',
    contract_start_date: formProcess.contract_start_date || '',
    contract_end_date: formProcess.contract_end_date || '',
    contract_value: formProcess.contract_value || 0,
    contract_percentage: formProcess.contract_percentage || 0,
    contract_additional_value: formProcess.contract_additional_value || 0,
  });
  const [percentageInput, setPercentageInput] = useState(
    formProcess.contract_percentage ? formatPercentage(formProcess.contract_percentage) : '',
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const hasAutofill = hasAutofilledField(autofilled_fields, form);
  const isSearchingCompany = !!request?.search_company_data?.loading;

  const minStartDate = useMemo(
    () => calculateMinStartDate(formProcess.type_warranty_id),
    [formProcess.type_warranty_id],
  );

  const isStartDateInvalid =
    form.contract_start_date.trim() !== '' && form.contract_start_date < minStartDate;

  const isFormValid =
    form.bidding_modality.trim() !== '' &&
    form.bidding_edict.trim() !== '' &&
    form.contract_days.trim() !== '' &&
    form.contract_start_date.trim() !== '' &&
    form.contract_end_date.trim() !== '' &&
    !isStartDateInvalid &&
    form.contract_value > 0 &&
    form.contract_percentage > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let newForm = { ...form };

    if (name === 'contract_value' || name === 'contract_additional_value') {
      newForm = { ...form, [name]: parseCurrencyInput(value) };
    } else if (name === 'contract_percentage') {
      setPercentageInput(value);
      let numericValue = parsePercentageInput(value);
      const maxValue = isLicitante ? 1 : 10;
      if (numericValue > maxValue) numericValue = maxValue;
      newForm = { ...form, [name]: numericValue };
    } else if (name === 'contract_days') {
      newForm = { ...form, [name]: value };
      if (form.contract_start_date && value) {
        newForm.contract_end_date = calculateEndDate(form.contract_start_date, parseInt(value, 10));
      }
    } else if (name === 'contract_start_date') {
      newForm = { ...form, [name]: value };
      if (form.contract_days) {
        newForm.contract_end_date = calculateEndDate(value, parseInt(form.contract_days, 10));
      }
    } else {
      newForm = { ...form, [name]: value };
    }
    setForm(newForm);
  };

  useEffect(() => {
    if (form.contract_start_date && form.contract_days) {
      const calculatedEndDate = calculateEndDate(
        form.contract_start_date,
        parseInt(form.contract_days, 10),
      );
      if (calculatedEndDate && calculatedEndDate !== form.contract_end_date) {
        setForm((prev) => ({ ...prev, contract_end_date: calculatedEndDate }));
      }
    }
  }, [form.contract_start_date, form.contract_days]);

  const totalCoverage = calculateCoverage(
    form.contract_value,
    form.contract_percentage,
    form.contract_additional_value,
  );

  const handleCancel = () => {
    dispatch(clearFormQuote());
    history.push('/conlicitaseg');
  };

  const handleContinue = () => {
    setShowConfirmModal(false);
    dispatch(
      setFormQuote({
        bidding_modality: form.bidding_modality,
        bidding_edict: form.bidding_edict,
        bidding_contract_number: form.bidding_contract_number,
        bidding_process_number: form.bidding_process_number,
        contract_days: form.contract_days ? parseInt(form.contract_days, 10) : 0,
        contract_start_date: form.contract_start_date,
        contract_end_date: form.contract_end_date,
        contract_value: form.contract_value,
        contract_percentage: form.contract_percentage,
        contract_insured_amount: totalCoverage,
        contract_additional_value: form.contract_additional_value,
        policyholder_register_junto,
      }),
    );
    history.push(pathFor('confirmacao'));
  };

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions py-5 px-md-5">
        <div className="row justify-content-center mt-5">
          <div className="col-12 mx-auto">
            <div className="row align-items-stretch">
              <div className="col-md-9">
                <div className="shadow-sm p-4 bg-white rounded">
                  <h3 className="mb-4">Estamos quase lá!</h3>
                  {hasAutofill && <AiAutofillNotice />}

                  <div className="d-flex justify-content-between mb-2">
                    <h5 className="mb-3">{config.contractInfoSubtitle}</h5>
                    {hasAutofill && <IaBadge />}
                  </div>
                  {hasAutofill && <AutofillValidationHint />}
                  <div className="mb-4">
                    <div className="row">
                      <div className={fieldColumn}>
                        <label htmlFor="bidding_modality">Modalidade</label>
                        <select
                          id="bidding_modality"
                          name="bidding_modality"
                          className="form-control"
                          value={form.bidding_modality}
                          onChange={handleChange}
                        >
                          <option value="">Selecione</option>
                          {modalities.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={fieldColumn}>
                        <label htmlFor="bidding_edict">N° do edital</label>
                        <input
                          id="bidding_edict"
                          name="bidding_edict"
                          className="form-control"
                          value={form.bidding_edict}
                          onChange={handleChange}
                          placeholder="Ex.: 01/2025"
                        />
                      </div>
                      {!isLicitante && (
                        <div className={fieldColumn}>
                          <label htmlFor="bidding_contract_number">N° do contrato (opcional)</label>
                          <input
                            id="bidding_contract_number"
                            name="bidding_contract_number"
                            className="form-control"
                            value={form.bidding_contract_number}
                            onChange={handleChange}
                          />
                        </div>
                      )}
                      <div className={fieldColumn}>
                        <label htmlFor="bidding_process_number">N° do processo</label>
                        <input
                          id="bidding_process_number"
                          name="bidding_process_number"
                          className="form-control"
                          value={form.bidding_process_number}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <h5 className="mb-3">Qual a vigência da apólice?</h5>
                    {hasAutofill && <IaBadge />}
                  </div>
                  <div className="mb-4">
                    <div className="row">
                      <div className="col-md-4 mb-4">
                        <label htmlFor="contract_days">
                          {isLicitante
                            ? 'Validade da garantia da proposta'
                            : 'Validade da garantia de execução'}
                        </label>
                        <input
                          id="contract_days"
                          name="contract_days"
                          className="form-control"
                          value={form.contract_days}
                          onChange={handleChange}
                          placeholder={isLicitante ? 'Ex.: 60 (dias)' : 'Ex.: 365 (dias)'}
                          type="number"
                          min="0"
                        />
                      </div>
                      <div className="col-md-4 mb-4">
                        <label htmlFor="contract_start_date">Data de início da vigência</label>
                        <input
                          id="contract_start_date"
                          name="contract_start_date"
                          className={`form-control ${isStartDateInvalid ? 'is-invalid' : ''}`}
                          value={form.contract_start_date}
                          onChange={handleChange}
                          type="date"
                          min={minStartDate}
                          aria-invalid={isStartDateInvalid}
                          aria-describedby="contract_start_date_feedback"
                        />
                        {isStartDateInvalid && (
                          <div
                            id="contract_start_date_feedback"
                            className="invalid-feedback d-block"
                          >
                            A data de início não pode ser anterior a {formatDate(minStartDate)}.
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 mb-4">
                        <label htmlFor="contract_end_date">Data fim da vigência</label>
                        <input
                          id="contract_end_date"
                          name="contract_end_date"
                          className="form-control"
                          value={form.contract_end_date}
                          type="date"
                          readOnly
                        />
                        <small className="form-text text-muted">
                          Esta data é calculada automaticamente baseada na data de início e vigência
                          em dias
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <h5 className="mb-3">
                        Qual o valor da licitação e o percentual da garantia que você precisa?
                      </h5>
                      {hasAutofill && <IaBadge />}
                    </div>
                    <div className="row">
                      <div className={fieldColumn}>
                        <label htmlFor="contract_value">
                          {isLicitante ? 'Valor estimado' : 'Valor do contrato'}
                        </label>
                        <input
                          id="contract_value"
                          name="contract_value"
                          className="form-control"
                          value={formatCurrency(form.contract_value, { emptyOnZero: true })}
                          onChange={handleChange}
                          type="text"
                        />
                      </div>
                      <div className={fieldColumn}>
                        <label htmlFor="contract_percentage">Percentual da garantia</label>
                        <input
                          id="contract_percentage"
                          name="contract_percentage"
                          className="form-control"
                          value={percentageInput}
                          onChange={handleChange}
                          onBlur={() => {
                            if (form.contract_percentage > 0) {
                              setPercentageInput(formatPercentage(form.contract_percentage));
                            }
                          }}
                          onFocus={() => {
                            if (form.contract_percentage > 0) {
                              setPercentageInput(
                                new Intl.NumberFormat('pt-BR', {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                }).format(form.contract_percentage),
                              );
                            }
                          }}
                          type="text"
                        />
                        <small className="form-text text-muted">
                          {isLicitante ? 'Máximo permitido: 1%' : 'Máximo permitido: 10%'}
                        </small>
                      </div>
                      {!isLicitante && (
                        <div className={fieldColumn}>
                          <label htmlFor="contract_additional_value">
                            Valor de garantia adicional{' '}
                            <InfoIcon
                              className="material-icons text-primary ml-1 databalloon"
                              aria-label="Preencha esse campo se o valor da sua proposta for inferior a 85% do valor orçado pela Administração"
                              data-balloon-pos="up"
                            >
                              info
                            </InfoIcon>
                          </label>
                          <input
                            id="contract_additional_value"
                            name="contract_additional_value"
                            className="form-control"
                            value={formatCurrency(form.contract_additional_value, {
                              emptyOnZero: true,
                            })}
                            onChange={handleChange}
                            type="text"
                          />
                        </div>
                      )}
                      <div className={fieldColumn}>
                        <label htmlFor="total_coverage">
                          {isLicitante ? 'Valor da cobertura' : 'Valor total da cobertura'}
                        </label>
                        <input
                          id="total_coverage"
                          name="total_coverage"
                          className="form-control"
                          value={
                            form.contract_value && form.contract_percentage
                              ? formatCurrency(totalCoverage)
                              : ''
                          }
                          placeholder="Calculado automaticamente"
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end align-items-center mt-4">
                    <button
                      type="button"
                      className="btn btn-link mr-2"
                      onClick={() => history.push(pathFor('tomador'))}
                    >
                      Voltar etapa
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCancel}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ml-2"
                      onClick={() => setShowConfirmModal(true)}
                      disabled={!isFormValid || isSearchingCompany}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        Continuar
                        {isSearchingCompany && (
                          <SquareLoader customClass="conlicitaseg-continue-quote" />
                        )}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-3 d-none d-md-block">
                <InsuranceStepIndicator {...stepIndicatorProps('licitacao')} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <div className="d-flex justify-content-end p-2">
          <button
            type="button"
            className="close px-3"
            aria-label="Fechar"
            onClick={() => setShowConfirmModal(false)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <Modal.Body className="text-center px-4 pb-4 pt-0">
          <p>
            Ao prosseguir, você confirma que as informações preenchidas estão corretas. Elas serão
            incluídas na cotação e na minuta da apólice do seguro.
          </p>
          <p>
            <strong>Deseja continuar?</strong>
          </p>
          <div className="d-flex justify-content-center mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancelar
            </button>
            <button type="button" className="btn btn-primary ml-2" onClick={handleContinue}>
              Continuar
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default QuoteContractInfo;
